import { useEffect, useReducer } from "react";
import { Log, User, UserManager, UserProfile } from "oidc-client-ts";
import { useAuth } from "react-oidc-context";
import * as Sentry from "@sentry/react";

import { IOidcSettings } from "../../types/common";
import { RESPONSE_ERROR_KEY } from "../../utils/common/contants";

export const FORCED_LOGOUT_KEY = "ams:forced-logout";

// Claims issued by the platform OIDC provider on top of the standard ones; the
// provider does not publish a schema, so their values stay untyped.
export interface IAppProfile extends UserProfile {
  customer_id?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  tenant?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

let userManager: UserManager | undefined;
// Set on each load while the forced-logout flag is in sessionStorage; hides a
// leftover stored user until a sign-in loads a new one.
let forcedLogoutAtStart = false;

function userIsAuthenticated(user?: User | null): boolean {
  return Boolean(user && !user.expired && user.id_token);
}

export async function getUserProfile(): Promise<IAppProfile | undefined> {
  const user = await userManager?.getUser();
  return user?.profile as IAppProfile | undefined;
}

export async function getCustomerId(): Promise<IAppProfile["customer_id"]> {
  return (await getUserProfile())?.customer_id;
}

export async function getIdToken(): Promise<string | null> {
  const user = await userManager?.getUser();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return userIsAuthenticated(user) ? user!.id_token! : null;
}

export async function getIdTokenSubject(): Promise<string | null> {
  const user = await userManager?.getUser();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return userIsAuthenticated(user) ? user!.profile.sub : null;
}

export async function isAuthenticated(): Promise<boolean> {
  const user = await userManager?.getUser();

  return userIsAuthenticated(user);
}

// The user held by the AuthProvider. Re-renders on accessTokenExpired:
// AuthProvider ignores that event, and user.expired is computed from the clock.
function useOidcUser(): User | undefined {
  const user = useAuth().user ?? undefined;
  const [, rerender] = useReducer((count: number) => {
    return count + 1;
  }, 0);

  useEffect(() => {
    const events = userManager?.events;
    events?.addAccessTokenExpired(rerender);

    return () => {
      events?.removeAccessTokenExpired(rerender);
    };
  }, []);

  return forcedLogoutAtStart ? undefined : user;
}

export function useIdToken(): string | null {
  const user = useOidcUser();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return userIsAuthenticated(user) ? user!.id_token! : null;
}

export function useIsAuthenticated(): boolean {
  return userIsAuthenticated(useOidcUser());
}

export function useOidcProfile(): IAppProfile | undefined {
  return useOidcUser()?.profile as IAppProfile | undefined;
}

export function useOidcUserId(): string | undefined {
  return useOidcProfile()?.sub;
}

export function useCustomerId(): IAppProfile["customer_id"] {
  return useOidcProfile()?.customer_id;
}

export function useUserManager(): UserManager {
  if (!userManager) {
    throw new Error("UserManager used before createAppUserManager");
  }

  return userManager;
}

// Signs out when the access token is about to expire. The flag tells the
// signout callback to show the session-expired page; it is cleared on the next
// sign-in.
export async function forceLogout(): Promise<void> {
  window.sessionStorage.setItem(FORCED_LOGOUT_KEY, "true");

  try {
    await userManager?.signoutRedirect();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Forced logout signout redirect failed", error);
    Sentry.captureException(error);
    window.location.replace(
      `/error?errorCode=${RESPONSE_ERROR_KEY.SessionHasExpired}`,
    );
  }
}

// oidc-client-ts settings are read-only, so the manager is built once the
// brand configuration is known; non-React code reaches it through this module.
export function createAppUserManager(settings?: IOidcSettings): UserManager {
  if (userManager) {
    return userManager;
  }

  const baseUrl = window.location.origin;

  // The fallback client secret is okay per
  //   <https://tools.ietf.org/html/draft-ietf-oauth-browser-based-apps-04#section-9>
  // as we are not treating the secret as proof of identity. Also, `oidc-client-ts`
  // uses PKCE for additional assurance.
  userManager = new UserManager({
    authority: settings
      ? settings.authority
      : (process.env.REACT_APP_OIDC_BASE_URL || baseUrl) + "/oidc",
    client_id: settings ? settings.client_id : "ph-public",
    client_secret: settings ? settings.client_secret : "ph-public-secret",

    redirect_uri: `${baseUrl}/oidc-signin-callback`,
    post_logout_redirect_uri: `${baseUrl}/oidc-signout-callback`,
    silent_redirect_uri: `${baseUrl}/oidc-silent-renew`,

    response_type: "code",
    prompt: settings?.prompt_consent === false ? undefined : "consent",
    scope: settings?.scope || "openid profile email groups",

    // The app signs out 30s before access-token expiry instead of renewing.
    automaticSilentRenew: false,
    filterProtocolClaims: true,
    loadUserInfo: settings?.load_user_info === true,
    accessTokenExpiringNotificationTimeInSeconds: 30,
  });

  forcedLogoutAtStart =
    window.sessionStorage.getItem(FORCED_LOGOUT_KEY) === "true";

  userManager.events.addUserLoaded(() => {
    forcedLogoutAtStart = false;
    window.sessionStorage.removeItem(FORCED_LOGOUT_KEY);
  });
  userManager.events.addAccessTokenExpiring(forceLogout);

  if (process.env.NODE_ENV === "development") {
    Log.setLogger(console);
    Log.setLevel(Log.DEBUG);

    Object.defineProperty(window, "userManager", {
      get() {
        return userManager;
      },
    });
  }

  return userManager;
}
